/**
 * 사이드바 메뉴 노출 판정 — SSOT.
 *
 * 사이드바(AppShell)와 사이드바 메뉴 기준으로 정렬되는 화면(전체 대시보드 메뉴별 주요 통계)이
 * 같은 기준으로 모듈 노출을 판정하도록 한 곳에서 정의함(판정 로직 중복 금지).
 */
import { useMemo } from "react";

import { useMenuGate, useMenuPermsReady } from "@/lib/dynamic/useMenuGate";
import { resolveLabel } from "@/lib/i18n/resolveLabel";
import {
  MENU_ACTION,
  MENU_DASHBOARD_OVERALL,
  MENU_DASHBOARD_PERSONAL,
  type MenuGate,
} from "@/lib/dynamic/menuPermissions";
import { type NavResponse, useNav } from "@/lib/queries/useNav";
import { useWorkitemTypes } from "@/lib/queries/useWorkitemTypes";
import { useWorkspaceDetail } from "@/lib/queries/useWorkspaces";

import { NAV_HIDDEN_MODULES, NAV_HIDDEN_TYPES } from "./hidden-modules";

/** 프로젝트 관리 메뉴의 nav 항목 ref(대문자). 노출은 DSN 프로비저닝을 따름. */
export const PROJECTS_NAV_REF = "ALM";
/** 프로젝트 관리 메뉴 노출을 결정하는 프로비저닝 모듈 코드. */
const PROJECTS_PROVISION_MODULE = "DSN";
/** 추적성 메뉴 노출을 결정하는 프로비저닝 모듈 코드. */
const TRACE_PROVISION_MODULE = "TRACE";

/** 사이드바 고정 메뉴의 워크스페이스 기준 상대 경로. */
export const NAV_PATH = {
  DASHBOARD_PERSONAL: "dashboard/personal",
  DASHBOARD_OVERALL: "dashboard/overall",
  TRACEABILITY: "traceability",
  PROJECTS: "projects",
} as const;
/** nav 시스템 항목 ref — 대시보드(개인/전체) 블록. */
const NAV_DASHBOARD_REF = "dashboard";
/** 추적성 메뉴의 nav 항목 ref(대문자). */
const TRACE_NAV_REF = "TRACE";

/** 사이드바 노출 판정 문맥 — 워크스페이스 프로비저닝 + 메뉴 권한. */
export interface NavVisibilityContext {
  /** true=프로비저닝 구성 완료(enabledModules 필터 적용), false=미구성(전체 노출) */
  modulesProvisioned: boolean;
  enabledModules: string[] | undefined;
  menuGate: MenuGate;
}

/** 워크스페이스 구성 판정 문맥 — 프로비저닝만(사용자 권한 무관). */
export type NavConfigContext = Pick<NavVisibilityContext, "modulesProvisioned" | "enabledModules">;

/**
 * 업무유형이 워크스페이스에 구성되어 있는지 (프로비저닝·미노출 모듈/유형). 사용자 권한은 보지 않음 —
 * 사이드바 노출·라우트 가드·홈 경로 판정이 공유하는 "구성" 기준.
 */
export function isNavTypeConfigured(
  wt: { moduleCode: string; typeCode: string },
  ctx: NavConfigContext,
): boolean {
  if (ctx.modulesProvisioned && !ctx.enabledModules?.includes(wt.moduleCode)) return false;
  if (NAV_HIDDEN_MODULES.has((wt.moduleCode ?? "").toUpperCase())) return false;
  return !NAV_HIDDEN_TYPES.has(wt.typeCode);
}

/** 업무유형이 사이드바 모듈 버킷에 들어가는지 — 워크스페이스 구성 + 유형 메뉴 조회(r) 권한. */
export function isNavTypeShown(
  wt: { moduleCode: string; typeCode: string },
  ctx: NavVisibilityContext,
): boolean {
  return (
    isNavTypeConfigured(wt, ctx) &&
    ctx.menuGate.canType(wt.typeCode, wt.moduleCode, MENU_ACTION.READ)
  );
}

/** 추적성 메뉴가 워크스페이스에 구성되어 있는지 — TRACE 프로비저닝 연동(미구성이면 전체 노출). */
export function isTraceMenuConfigured(ctx: NavConfigContext): boolean {
  return !ctx.modulesProvisioned || !!ctx.enabledModules?.includes(TRACE_PROVISION_MODULE);
}

/** 프로젝트 관리 메뉴 노출 여부 — 프로젝트관리(DSN) 프로비저닝 연동. */
export function isProjectsMenuShown(ctx: NavConfigContext): boolean {
  return !ctx.modulesProvisioned || !!ctx.enabledModules?.includes(PROJECTS_PROVISION_MODULE);
}

/**
 * 사이드바에 보이는 모듈 코드(대문자) 집합. 프로젝트 관리는 {@link PROJECTS_NAV_REF}로 포함.
 * nav 구조가 로드되면 그 구성에 있는 모듈 항목만 남김(사이드바 데이터 기반 렌더와 동일).
 * 워크스페이스·업무유형 로딩 중에는 undefined, 조회 실패 시 빈 집합.
 */
export function useSidebarModuleCodes(slug: string | undefined): Set<string> | undefined {
  const { data: wsDetail, isError: wsError } = useWorkspaceDetail(slug);
  const { data: workitemTypes, isError: typesError } = useWorkitemTypes(slug);
  const { data: nav } = useNav(slug);
  const menuGate = useMenuGate();

  return useMemo(() => {
    // 이미 받은 데이터가 있으면 백그라운드 재조회 실패와 무관하게 그 데이터로 판정(사이드바와 동일).
    if (!wsDetail || !workitemTypes) {
      return (wsError && !wsDetail) || (typesError && !workitemTypes)
        ? new Set<string>()
        : undefined;
    }
    const ctx: NavVisibilityContext = {
      modulesProvisioned: wsDetail.modulesProvisioned ?? false,
      enabledModules: wsDetail.enabledModules,
      menuGate,
    };
    const shown = new Set<string>();
    for (const wt of workitemTypes) {
      const code = (wt.moduleCode ?? "").toUpperCase();
      if (code && isNavTypeShown(wt, ctx) && menuGate.canRead(code)) shown.add(code);
    }
    if (isProjectsMenuShown(ctx)) shown.add(PROJECTS_NAV_REF);
    if (!nav) return shown;
    const navRefs = new Set(
      nav.sections.flatMap((s) =>
        s.entries.filter((e) => e.kind === "module").map((e) => e.ref.toUpperCase()),
      ),
    );
    return new Set([...shown].filter((code) => navRefs.has(code)));
  }, [wsDetail, workitemTypes, wsError, typesError, nav, menuGate]);
}

/** 사이드바 모듈 안 업무유형 정렬 기준(sort → typeCode) — 사이드바·기본 진입 경로 공용. */
export function compareNavTypes(
  a: { sort?: number; typeCode: string },
  c: { sort?: number; typeCode: string },
): number {
  return (a.sort ?? 0) - (c.sort ?? 0) || a.typeCode.localeCompare(c.typeCode);
}

/** 다국어 라벨 원천(모듈 메타·업무유형 공용). */
type LabelSource = { name: string; nameI18n?: Record<string, string | undefined> | null };

/**
 * 사이드바 모듈(상위 메뉴) 라벨 — 모듈 메타(nameI18n) 우선, 없으면 모듈의 첫 업무유형 표시명, 그것도 없으면 모듈 코드.
 * 사이드바(AppShell)·설정 › 메뉴 권한 탭 공용.
 */
export function resolveModuleLabel(
  meta: LabelSource | undefined,
  firstType: LabelSource | undefined,
  moduleCode: string,
  lang: string,
): string {
  if (meta) return resolveLabel(meta.name, meta.nameI18n, lang);
  return resolveLabel(firstType?.name ?? moduleCode, firstType?.nameI18n, lang);
}

/** 업무유형 화면 상대 경로(`w/<typecode 소문자>`). */
const typePath = (typeCode: string): string => `w/${typeCode.toLowerCase()}`;

/** 기본 진입 경로 판정에 필요한 업무유형 필드. */
type HomeType = { typeCode: string; moduleCode: string; sort?: number };

/** 사이드바 모듈 버킷과 같은 순서(sort → typeCode)로 정렬한 노출 유형 중 첫 번째. */
function firstShownType(
  types: readonly HomeType[],
  ctx: NavVisibilityContext,
  moduleCode?: string,
): HomeType | undefined {
  return types
    .filter(
      (wt) =>
        (moduleCode === undefined || (wt.moduleCode ?? "").toUpperCase() === moduleCode) &&
        isNavTypeShown(wt, ctx) &&
        ctx.menuGate.canRead((wt.moduleCode ?? "").toUpperCase()),
    )
    .sort(compareNavTypes)[0];
}

/** 대시보드 블록에서 보이는 첫 하위 메뉴 경로(개인 → 전체). 둘 다 숨으면 null. */
export function firstDashboardPath(gate: MenuGate): string | null {
  if (gate.canDashboard(MENU_DASHBOARD_PERSONAL)) return NAV_PATH.DASHBOARD_PERSONAL;
  if (gate.canDashboard(MENU_DASHBOARD_OVERALL)) return NAV_PATH.DASHBOARD_OVERALL;
  return null;
}

/**
 * 기본 진입 경로 — 사이드바(AppShell)와 같은 순서·노출 기준으로 첫 노출 메뉴의 상대 경로. 없으면 null.
 *
 * @param nav 사이드바 nav 구조. undefined=조회 실패 — 사이드바 레거시 폴백처럼 대시보드 → 추적성 → 업무유형 → 프로젝트 관리
 *   순으로 본다(유형은 모듈 순서를 모르므로 sort → typeCode 순 근사).
 */
export function resolveHomePath(
  nav: NavResponse | undefined,
  types: readonly HomeType[],
  ctx: NavVisibilityContext,
): string | null {
  const entryPath = (kind: "module" | "system", rawRef: string): string | null => {
    if (kind === "system") {
      return rawRef === NAV_DASHBOARD_REF ? firstDashboardPath(ctx.menuGate) : null;
    }
    const ref = rawRef.toUpperCase();
    if (ref === TRACE_NAV_REF) return isTraceMenuConfigured(ctx) ? NAV_PATH.TRACEABILITY : null;
    if (ref === PROJECTS_NAV_REF) return isProjectsMenuShown(ctx) ? NAV_PATH.PROJECTS : null;
    const wt = firstShownType(types, ctx, ref);
    return wt ? typePath(wt.typeCode) : null;
  };

  if (nav) {
    for (const section of nav.sections) {
      for (const entry of section.entries) {
        const path = entryPath(entry.kind, entry.ref);
        if (path) return path;
      }
    }
    return null;
  }
  const first = firstShownType(types, ctx);
  return (
    firstDashboardPath(ctx.menuGate) ??
    (isTraceMenuConfigured(ctx) ? NAV_PATH.TRACEABILITY : null) ??
    (first ? typePath(first.typeCode) : null) ??
    (isProjectsMenuShown(ctx) ? NAV_PATH.PROJECTS : null)
  );
}

/**
 * 워크스페이스 기본 진입 경로(§5 — 개인 대시보드 권한이 없으면 사이드바에서 보이는 첫 메뉴).
 * undefined=로딩 중(권한·워크스페이스·업무유형·nav 수신 전), null=노출 메뉴 없음, 문자열=상대 경로.
 * 조회 실패는 사이드바와 같은 폴백(워크스페이스 실패=미구성 취급, 유형 실패=유형 없음, nav 실패=레거시 순서).
 */
export function useHomePath(slug: string | undefined): string | undefined | null {
  const { data: wsDetail, isPending: wsPending } = useWorkspaceDetail(slug);
  const { data: workitemTypes, isPending: typesPending } = useWorkitemTypes(slug);
  const { data: nav, isPending: navPending } = useNav(slug);
  const menuGate = useMenuGate();
  const permsReady = useMenuPermsReady();
  const loading = !slug || wsPending || typesPending || navPending || !permsReady;

  return useMemo(() => {
    if (loading) return undefined;
    const ctx: NavVisibilityContext = {
      modulesProvisioned: wsDetail?.modulesProvisioned ?? false,
      enabledModules: wsDetail?.enabledModules,
      menuGate,
    };
    return resolveHomePath(nav, workitemTypes ?? [], ctx);
  }, [loading, wsDetail, workitemTypes, nav, menuGate]);
}
